/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, type OnDestroy, type OnInit, Component, Input } from '@angular/core';
import { type Polling, type RestJob, EventService, HttpService, LazyloadService, PermissionService, PollingService, UNPLACEHOLDER } from '@dm/core';
import { AuthService } from 'app/common/services';
import { UpgradeService as MainUpgradeService } from 'app/feature/settings/basic-infomation/upgrade/upgrade.service';
import { type Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DetailCheckResultUpgradeComponent } from '../detail-check-result-upgrade/detail-check-result-upgrade.component';
import { type CheckDetail, CheckResult, CheckStatusE, CmoQueryFrom, CmoQueryTypeE, CmoUpdSysModeE, CmoUpdSysStepTaskStatusE,
    Progress,
    UpgradeModeE} from '../upgrade.const';
import { UpgradeService } from '../upgrade.service';
import { UpgradeStrService } from '../upgrade-str.service';

@Component({
    selector: 'app-upgrade',
    templateUrl: './upgrade.component.html',
    styleUrls: ['./upgrade.component.scss'],
    host: {
        '[class.hidden]': '!iconVisible',
    },
})
export class UpgradeComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Input() iconVisible: boolean;

    i18n = this.str.i18n;
    progressDesc = this.str.progressDesc;

    Progress = Progress;

    CheckResult = CheckResult;

    // 检查详情轮询(预检查、验证)
    checkResultPolling: Polling;
    // 升级详情轮询
    upgradeProgressPolling: Polling;

    upgradePolling: Polling;
    // 当前升级状态 preCheck、upgrade、verificatng
    upgradeStatus = {
        checkProgress: 0,
        status: CmoUpdSysStepTaskStatusE.Upgrading,
    };
    _currentProcess: Progress;
    // 检查详情
    checkDetails = [];
    // 升级详情
    upgradeDetails: any;
    // 操作按钮状态
    btnEnabled = false;
    isCheck = false;
    sucCount = 0;
    failCount = 0;
    currentVersion = '--';
    UNPLACEHOLDER = UNPLACEHOLDER;

    // 进度条动画。切换步骤时进度会重置，此时不应该有动画
    progressMotion = true;

    inUpgradeProcessSub$: Subscription;

    /**
     * 保存当前打开的详情页组件，用于数据双向沟通
     */
    detailComponent: ComponentRef<DetailCheckResultUpgradeComponent>;

    /**
     * 当前打开的检查详情数据key
     * 以检查详情的checkItem值作为key
     */
    checkDetailDataKey: string;

    /**
     * 升级面板保持不关闭。
     */
    noClosePanel = false;

    set currentProcess(value: Progress) {
        this._currentProcess = value;
        // 赋值的同时修改公共状态
        this.mainUpgradeService.upgradeProcess = value;
        this.mainUpgradeService.upgradeMode = UpgradeModeE.HotPatch;
    }

    get currentProcess(): Progress {
        return this._currentProcess;
    }

    constructor(
        private http: HttpService,
        private poll: PollingService,
        public str: UpgradeStrService,
        private service: UpgradeService,
        private authService: AuthService,
        private permissionService: PermissionService,
        private eventService: EventService,
        private mainUpgradeService: MainUpgradeService,
        private lazyLoad: LazyloadService,
    ) {}

    ngOnInit(): void {
        this.isCheck = true;

        // 超级管理员才重入
        if (this.permissionService.isSuperAdmin()) {
            this.reenter();
        }
    }

    reenter(): void {
        this.inUpgradeProcessSub$ = this.eventService.subscribe('inUpgradeProcess', (data: { type: CmoUpdSysModeE; process: Progress}) => {
            // 热补丁检查/升级流程中
            if (data.type === CmoUpdSysModeE.Hotpatch && data.process !== Progress.prechecking) {
                this.currentProcess = data.process;
                this.upgradeStatus.checkProgress = 0;
                this.show();
                this.btnEnabled = false;
                this.checkDetails = [];
                // 升级后校验
                if (data.process === Progress.verifying) {
                    this.createVerfiy();
                } else if (data.process === Progress.upgrading) {
                    this.isCheck = false;
                    this.createUpgradePolling();
                }
            }
        });
    }

    show(): void {
        this.iconVisible = true;
        this.visible = true;
        // 进入升级流程
        this.mainUpgradeService.inUpgradeProcess = true;
    }

    updateProgressMotion(): void {
        this.progressMotion = false;
        setTimeout(() => {
            this.progressMotion = true;
        });
    }

    // 执行预检查
    precheck(): Promise<unknown> {
        this.currentProcess = Progress.prechecking;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.isCheck = true;
        this.updateProgressMotion();
        return new Promise((resolve) => {
            // 下发预检查命令  HotpatchPrecheck
            this.http.put(this.http.makeUrl('upgrade.check'), { checkType: 'HotpatchPrecheck', checkCMD: 0 }).subscribe((res) => {
                if (Number(res.result.code) === 0) {
                    this.show();
                    this.checkDetails = [];
                    this.createPrecheckPolling();
                    resolve(this.service.setPrecheckStatus('systemUpgrade'));
                }
            }, () => {
                this.checkDetails = [];
                this.btnEnabled = true;
                this.currentProcess = Progress.precheckFailed;
            });
        });
    }

    // 执行补丁包安装
    onUpgrade(): void {
        this.currentProcess = Progress.upgrading;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.updateProgressMotion();
        // 步骤执行情况计数清零
        this.sucCount = 0;
        this.failCount = 0;
        // CMO_EXC_ERROR_INFO  ibase字段名有问题，待修改
        this.http.put(this.http.makeUrl('upgrade.executeUpgrade'), { CMO_EXE_UPD_ACTIVETYPE: 5 })
            .subscribe((res) => {
            if (Number(res.result.code) === 0) {
                // 升级轮训
                this.isCheck = false;
                this.checkDetails = [];
                this.createUpgradePolling();
            }
        }, () => {
            this.checkDetails = [];
            this.btnEnabled = true;
            this.currentProcess = Progress.upgradeFailed;
        });
    }

    // 执行验证
    onVerify(): void {
        this.currentProcess = Progress.verifying;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.updateProgressMotion();
        // 下发预检查命令  HotpatchVerify
        this.http.put(this.http.makeUrl('upgrade.check'), { checkType: 'HotpatchVerify', checkCMD: 0 })
            .pipe(finalize(() => {
                this.isCheck = true;
                this.checkDetails = [];
            }))
            .subscribe(() => {
                this.createVerfiy();
            }, () => {
                // 执行失败置为验证失败
                this.btnEnabled = true;
                this.currentProcess = Progress.verifyFailed;
            });
    }

    // 开启预检查轮询
    createPrecheckPolling(): void {
        // 不重复创建轮询
        if (this.checkResultPolling) {
            return;
        }

        this.currentProcess = Progress.prechecking;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const restJob: RestJob[] = [{
            rest: this.http.makeUrl('upgrade.checkResult').setParams({ range: '[0-100]' }),
            method: 'GET',
        }, {
            rest: this.http.makeUrl('upgrade.checkProgress').setParams({ checkType: 0 }),
            method: 'GET',
        }];
        this.checkResultPolling = this.poll.addCombo(restJob, 20);
        this.checkResultPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            let sucCount = 0;
            let failCount = 0;
            res[0].data.forEach((item) => {
                const checkResult: CheckResult = Number(item.checkResult);

                if (checkResult === CheckResult.Suc) {
                    sucCount++;
                }
                if (this.isFailedItem(checkResult)) {
                    failCount++;
                }

                // 将换行符\n替换为<br>
                this.checkDetailDataHandler(item);
            });

            this.sucCount = sucCount;
            this.failCount = failCount;
            // 排序
            this.checkDetails = this.service.checkDetailsSort(res[0].data);

            // 每次轮询更新一下详情数据
            this.updateDetailData();

            this.upgradeStatus = res[1].data;
            const checkStatus = Number(res[1].data.checkStatus);
            if (checkStatus === CheckStatusE.Done || checkStatus === CheckStatusE.Failed) {
                this.poll.remove(this.checkResultPolling);
                this.checkResultPolling = null;
                 // 总进度失败或者含有失败项都认为失败
                if (checkStatus === CheckStatusE.Failed ||
                    this.checkDetails.some((value) => this.isFailedItem(Number(value.checkResult)))) {
                    this.btnEnabled = true;
                    this.currentProcess = Progress.precheckFailed;
                } else {
                    // 若全部成功则直接执行升级
                    this.currentProcess = Progress.precheckSuc;
                    setTimeout(() => this.onUpgrade(), 3000);
                }
            }
        });
    }

    /**
     * 检查详情数据处理
     * @param checkDetail
     */
    checkDetailDataHandler(item: CheckDetail): void {
        // 检查方法、检查标准、建议字段数据换行符转换
        ['method', 'criterion', 'suggestion'].forEach((key) => {
            if (item[key]) {
                item[key] = this.newLineCharacterConvert(item[key]);
            }
        });

        // 节点信息转换为对象数组，并对内容做换行符转换
        if (typeof item.nodeInfo === 'object') {
            item.nodeInfoArr = Object.keys(item.nodeInfo).map((key) => ({
                node: key,
                info: this.newLineCharacterConvert(item.nodeInfo[key] as string),
            }));
        }
    }

    /**
     * 换行符转换。
     * 将换行符\n替换为<br>
     * @param item
     */
    newLineCharacterConvert(item: string): string {
        return item.replace(/\\n/g, '<br>');
    }

    /**
     * 展示检查详情
     * @param checkItem 检查项字符串
     */
    onShowCheckDetails(checkItem: string): void {
        const checkDetailData = this.service.getCheckDetailData(checkItem, this.checkDetails);

        if (!checkDetailData) {
            return;
        }

        this.checkDetailDataKey = checkItem;
        this.noClosePanel = true;
        if (!this.detailComponent) {
            this.detailComponent = this.lazyLoad.appendComponent(DetailCheckResultUpgradeComponent, {
                onClose: () => {
                    this.detailComponent = null;
                    this.checkDetailDataKey = undefined;
                    this.noClosePanel = false;
                },
                data: checkDetailData,
            });
        } else {
            // 阻止当前侧滑框被关闭
            if (this.detailComponent.instance.slideWindow) {
                this.detailComponent.instance.slideWindow.isPreventClickHide = true;
            }
        }
    }

    // 开启补丁安装轮询
    createUpgradePolling(): void {
        // 不重复创建轮询
        if (this.upgradeProgressPolling) return;

        this.currentProcess = Progress.upgrading;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const restJob: RestJob[] = [{
            // 查询补丁安装进度
            rest: this.http.makeUrl('upgrade.upgradeProgress')
                .setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.Hotpatch, CMO_QUERY_FROM: CmoQueryFrom.DM }),
            method: 'GET',
        }, {
            // 查询补丁安装详情
            rest: this.http.makeUrl('upgrade.upgradeDetail')
                .setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.Hotpatch }),
            method: 'GET',
        }];
        this.upgradeProgressPolling = this.poll.addCombo(restJob, 10);
        this.upgradeProgressPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            this.checkDetails = [];
            let sucCount = 0;
            let failCount = 0;

            res[1].data.forEach((item, i) => {
                this.checkDetails[i] = {
                    checkResult: item.CMO_DETAIL_ATOM_STATE,
                    checkItem: item.CMO_DETAIL_NODE_ID + this.i18n.colon + item.CMO_STEP_NAME,
                    showDetail: false,
                    nodeInfo: item.CMO_DETAIL_ATOM_RECORD === '' ? '--' : item.CMO_DETAIL_ATOM_RECORD,
                };
                if (Number(item.CMO_DETAIL_ATOM_STATE) === CheckResult.Suc) {
                    sucCount = sucCount + 1;
                }
                if (Number(item.CMO_DETAIL_ATOM_STATE) === CheckResult.Fail || Number(item.CMO_DETAIL_ATOM_STATE) === CheckResult.Error) {
                    failCount = failCount + 1;
                }
            });

            // 重新排序
            this.checkDetails = this.service.checkDetailsSort(this.checkDetails);

            // 更新步骤执行情况计数
            this.sucCount = sucCount;
            this.failCount = failCount;
            // 获取升级进度值(进度可能不准确，不能用于判断)
            this.upgradeStatus.checkProgress = Number(res[0].data.CMO_UPD_SYS_STEP_STEP_PERCENT);
            // 获取升级状态
            this.upgradeStatus.status = Number(res[0].data.CMO_UPD_SYS_STEP_TASK_STATUS);
            // 如果不是Upgrading(10)，停止轮询
            if (this.upgradeStatus.status !== CmoUpdSysStepTaskStatusE.Upgrading) {
                // 如果不是成功，则标记为失败
                if (this.upgradeStatus.status !== CmoUpdSysStepTaskStatusE.UpgradeSuc) {
                    this.btnEnabled = true;
                    this.currentProcess = Progress.upgradeFailed;
                } else {
                    // 若成功则直接将进度设置为100%
                    this.upgradeStatus.checkProgress = 100;
                    this.currentProcess = Progress.upgradeSuc;
                    // 延迟3秒执行升级后校验
                    setTimeout(() => {
                        this.onVerify();
                    }, 3000);
                }

                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
            }
        });
    }

    // 开启验证轮训
    createVerfiy(): void {
        // 不重复创建轮询
        if (this.checkResultPolling) return;

        this.currentProcess = Progress.verifying;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const restJob: RestJob[] = [{
            rest: this.http.makeUrl('upgrade.checkResult').setParams({ range: '[0-100]' }),
            method: 'GET',
        }, {
            rest: this.http.makeUrl('upgrade.checkProgress').setParams({ checkType: 0 }),
            method: 'GET',
        }];
        this.checkResultPolling = this.poll.addCombo(restJob, 20);
        this.checkResultPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            let sucCount = 0;
            let failCount = 0;
            res[0].data.forEach((item: CheckDetail) => {
                const checkResult: CheckResult = Number(item.checkResult);
                // 将换行符\n替换为<br>
                this.checkDetailDataHandler(item);

                if (checkResult === CheckResult.Suc) {
                    sucCount = sucCount + 1;
                }
                if (this.isFailedItem(checkResult)) {
                    failCount = failCount + 1;
                }
            });
            this.sucCount = sucCount;
            this.failCount = failCount;
            // 排序
            this.checkDetails = this.service.checkDetailsSort(res[0].data);
            // 每次轮询更新一下详情数据
            this.updateDetailData();

            this.upgradeStatus = res[1].data;
            const checkStatus: CheckStatusE = Number(res[1].data.checkStatus);
            if (checkStatus === CheckStatusE.Done || checkStatus === CheckStatusE.Failed) {
                this.poll.remove(this.checkResultPolling);
                this.checkResultPolling = null;
                // 总进度失败或者含有失败项都认为失败
                if (checkStatus === CheckStatusE.Failed || this.checkDetails.find((value) => this.isFailedItem(Number(value.checkResult)))) {
                    this.btnEnabled = true;
                    this.currentProcess = Progress.verifyFailed;
                } else {
                    setTimeout(() => {
                        this.http.get(this.http.makeUrl('system.get')).subscribe((result) => {
                            this.currentVersion = result.data.SoftwarePatch ?? result.data.pointRelease;
                            this.currentProcess = Progress.verifySuc;
                        });
                        this.checkDetails = [];
                    }, 3000);
                }
            }
        });
    }

    /**
     * 更新详情数据
     * @param data
     */
    updateDetailData(): void {
        if (!this.detailComponent || this.checkDetailDataKey === undefined) {
            return;
        }
        const checkDetailData = this.service.getCheckDetailData(this.checkDetailDataKey, this.checkDetails);
        this.detailComponent.instance.data = checkDetailData;
        this.detailComponent.instance.onDataUpdate(checkDetailData, false);
    }

    onShowUpgradeDetails(index: number): void {
        this.checkDetails[index].showDetail = !this.checkDetails[index].showDetail;
    }

    /**
     * 是否是失败项
     * 失败和出错都认为是失败项
     * @param checkResult
     * @returns boolean
     */
    isFailedItem(checkResult: CheckResult): boolean {
        return [CheckResult.Fail, CheckResult.Error].includes(checkResult);
    }

    /**
     * 升级面板关闭前的检查，当侧划框被打开时，不允许关闭
     */
    beforePanelHide = (): boolean => !this.noClosePanel;

    // 取消
    onCancel(): void {
        if (this.btnEnabled === true) {
            // 如果按钮激活，则关闭窗口
            this.iconVisible = false;
            this.visible = false;
            this.currentProcess = undefined;
            // 退出升级流程
            this.mainUpgradeService.inUpgradeProcess = false;
            // 关闭超时检测例外
            this.authService.disableTimeoutExcept();
        }
    }

    // 重试
    onRetry(): void {
        if (this.currentProcess === Progress.precheckFailed) {
            this.checkDetails = [];
            this.precheck();
            this.btnEnabled = false;
        } else if (this.currentProcess === Progress.upgradeFailed) {
            this.checkDetails = [];
            this.onUpgrade();
            this.btnEnabled = false;
        } else if (this.currentProcess === Progress.verifyFailed) {
            this.checkDetails = [];
            this.onVerify();
            this.btnEnabled = false;
        }
    }

    onClose(): void {
        this.iconVisible = false;
        this.visible = false;
        window.location.reload();
    }

    ngOnDestroy(): void {
        // 关闭超时检测例外
        this.authService.disableTimeoutExcept();

        if (this.inUpgradeProcessSub$) {
            this.inUpgradeProcessSub$.unsubscribe();
        }

        if (this.upgradeProgressPolling) {
            this.upgradeProgressPolling.unsubscribe();
        }
    }
}
